import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { api, ApiError } from '../api/client';

export type RoleCode = 'admin' | 'super_user' | 'analyst' | 'viewer';

export interface AuthUser {
  id: string;
  email: string;
  display_name: string;
  tenant_id: string;
}

export interface LoginStep1Response {
  mfa_required: boolean;
  challenge_id?: string;
  expires_in_seconds?: number;
  channel?: string;
  masked_email?: string;
  user?: AuthUser;
  roles?: RoleCode[];
  expires_at?: string;
  must_change_password?: boolean;
}

export interface OtpVerifyResponse {
  user: AuthUser;
  roles: RoleCode[];
  expires_at: string;
  must_change_password: boolean;
}

export interface ResendResponse {
  challenge_id: string;
  expires_in_seconds: number;
  masked_email: string;
}

interface MeResponse {
  user: AuthUser;
  roles: RoleCode[];
  must_change_password?: boolean;
}

interface AuthContextValue {
  user: AuthUser | null;
  roles: RoleCode[];
  isLoading: boolean;
  /** The account still has its temporary password: every page sends the user to set a new one. */
  mustChangePassword: boolean;
  passwordChanged: () => void;
  login: (email: string, password: string) => Promise<LoginStep1Response>;
  verifyOtp: (challengeId: string, code: string) => Promise<OtpVerifyResponse>;
  resendOtp: (challengeId: string) => Promise<ResendResponse>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  hasRole: (...allowed: RoleCode[]) => boolean;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [roles, setRoles] = useState<RoleCode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const passwordChanged = useCallback(() => setMustChangePassword(false), []);

  const refresh = useCallback(async () => {
    try {
      const me = await api.get<MeResponse>('/auth/me');
      setUser(me.user);
      setRoles(me.roles);
      setMustChangePassword(Boolean(me.must_change_password));
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        setUser(null);
        setRoles([]);
      } else {
        console.warn('Auth /me check failed:', e);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(async (email: string, password: string): Promise<LoginStep1Response> => {
    const res = await api.post<LoginStep1Response>('/auth/login', { email, password });
    if (!res.mfa_required && res.user) {
      setUser(res.user);
      setRoles(res.roles ?? []);
      setMustChangePassword(Boolean(res.must_change_password));
    }
    return res;
  }, []);

  const verifyOtp = useCallback(async (challengeId: string, code: string): Promise<OtpVerifyResponse> => {
    const res = await api.post<OtpVerifyResponse>('/auth/otp/verify', {
      challenge_id: challengeId,
      code,
    });
    setUser(res.user);
    setRoles(res.roles);
    setMustChangePassword(Boolean(res.must_change_password));
    return res;
  }, []);

  const resendOtp = useCallback(async (challengeId: string): Promise<ResendResponse> => {
    return api.post<ResendResponse>('/auth/otp/resend', { challenge_id: challengeId });
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout', {});
    } catch {
      /* clear local state even if server unreachable */
    }
    setUser(null);
    setRoles([]);
    setMustChangePassword(false);
  }, []);

  const hasRole = useCallback(
    (...allowed: RoleCode[]) => roles.some((r) => allowed.includes(r)),
    [roles],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ user, roles, isLoading, mustChangePassword, passwordChanged, login, verifyOtp, resendOtp, logout, refresh, hasRole }),
    [user, roles, isLoading, mustChangePassword, passwordChanged, login, verifyOtp, resendOtp, logout, refresh, hasRole],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}